/**
 * Bloque unificado Generales + Distribución/superficies + Equipamiento + Ubicación (vista previa).
 */
import { CampoResumen, hayDato } from "./FichaResumenVistaSecciones";
import { equipamientoSubtexto, hayEquipamientoSubtexto } from "../../utils/fichaResumenEquipamiento";
import { formatoAlturaPlantaCrm } from "../../utils/formatoAlturaPlantaCrm";

const EQUIP_PAIRS = [
  ["eq_garaje", "Garaje / parking"],
  ["eq_calefaccion", "Calefacción"],
  ["eq_aire", "Aire acondicionado"],
  ["eq_ascensor", "Ascensor"],
  ["eq_chimenea", "Chimenea"],
  ["eq_gas", "Gas"],
  ["eq_agua", "Agua"],
  ["eq_agua_riego", "Agua de riego"],
  ["eq_pozo_agua", "Pozo de agua"],
  ["eq_cocina_verano", "Cocina de verano"],
  ["eq_luz", "Luz"],
  ["eq_luz_solar", "Luz solar"],
  ["eq_luz_solar_preinst", "Preinst. luz solar"],
  ["eq_lavadero", "Lavadero"],
  ["eq_ducha_exterior", "Ducha exterior"],
  ["eq_doble_cristal", "Doble cristal"],
  ["eq_parcela_vallada", "Parcela vallada"],
  ["eq_trastero", "Trastero"],
  ["eq_piscina_priv", "Piscina privada"],
  ["eq_piscina_comun", "Piscina comunitaria"],
  ["eq_zonas_comunes", "Zonas comunes"],
  ["eq_zona_infantil", "Zona infantil"],
  ["eq_zonas_verdes", "Zonas verdes"],
  ["eq_gimnasio", "Gimnasio"],
  ["eq_barbacoa", "Barbacoa"],
  ["eq_padel", "Pista pádel"],
  ["eq_tenis", "Pista tenis"],
  ["eq_conserje", "Conserje"],
  ["eq_casita_aperos", "Casita aperos"],
  ["eq_alarma", "Alarma"],
  ["eq_puerta_blindada", "Puerta blindada"],
  ["eq_puerta_antiokupa", "Puerta antiokupa"],
  ["eq_caja_fuerte", "Caja fuerte"],
  ["eq_vigilancia", "Vigilancia"],
  ["eq_domotica", "Domótica"],
  ["eq_punto_recarga", "Punto recarga EV"],
  ["eq_acceso_discap", "Acceso discapacidad"],
  ["eq_adaptado", "Adaptado"],
  ["eq_salida_humos", "Salida humos"],
  ["eq_edif_uso_viviendas", "Edif. uso viviendas"],
];

const UB_BOOL = [
  ["ub_primera_linea", "1ª línea playa"],
  ["ub_segunda_linea", "2ª línea playa"],
  ["ub_distancia_mar", "Distancia al mar"],
  ["ub_afectacion_costas", "No afectado"],
  ["ub_dpmt", "DPMT"],
  ["ub_serv_transito", "Serv. tránsito"],
  ["ub_serv_proteccion", "Serv. protección"],
];

function pick(d, p, key) {
  const a = d?.[key];
  const b = p?.[key];
  if (a !== undefined && a !== null && a !== "") return a;
  if (b !== undefined && b !== null && b !== "") return b;
  return null;
}

function superficieFilaTieneDato(s) {
  if (!s || typeof s !== "object") return false;
  return (
    hayDato(s.superficie) ||
    hayDato(s.metros) ||
    hayDato(s.nombre) ||
    hayDato(s.tipo) ||
    hayDato(s.altura) ||
    hayDato(s.observaciones)
  );
}

function hasGenerales(d, p) {
  const checks = [
    pick(d, p, "operacion"),
    pick(d, p, "tipo"),
    d?.estado_inmueble_nombre,
    d?.estado_conservacion,
    pick(d, p, "ano_construccion"),
    pick(d, p, "ano_reforma"),
    pick(d, p, "num_dormitorios"),
    pick(d, p, "num_banos"),
    pick(d, p, "num_aseos"),
    pick(d, p, "plantas_inmueble"),
    pick(d, p, "plantas_edificio"),
    pick(d, p, "altura_planta"),
    pick(d, p, "piso"),
    pick(d, p, "puerta"),
    pick(d, p, "bloque"),
    pick(d, p, "escalera"),
    pick(d, p, "orientacion"),
    pick(d, p, "cal_energetica"),
    pick(d, p, "cedula_habitabilidad"),
    pick(d, p, "vpo"),
    pick(d, p, "vpo_ano_fin"),
    pick(d, p, "exterior"),
    pick(d, p, "amueblado"),
    pick(d, p, "cocina_amueblada"),
    pick(d, p, "cocina_amueblada_obs"),
    pick(d, p, "armarios"),
    pick(d, p, "armarios_obs"),
    pick(d, p, "exclusiva"),
    pick(d, p, "exclusiva_duracion"),
    pick(d, p, "exclusiva_desde"),
    pick(d, p, "exclusiva_hasta"),
    pick(d, p, "suelos_nombre") || pick(d, p, "suelos_id"),
    pick(d, p, "carpinteria_int_nombre") || pick(d, p, "carpinteria_int_id"),
    pick(d, p, "carpinteria_ext_nombre") || pick(d, p, "carpinteria_ext_id"),
  ];
  return checks.some(hayDato);
}

function hasDistribucion(d, p) {
  const sups = d?.superficies || [];
  if (sups.some(superficieFilaTieneDato)) return true;
  const m2 = [
    pick(d, p, "m2_construidos"),
    pick(d, p, "m2_utiles"),
    pick(d, p, "m2_terrazas"),
    pick(d, p, "m2_jardin"),
    pick(d, p, "m2_parcela"),
    pick(d, p, "superficie_construida"),
    pick(d, p, "superficie_util"),
    pick(d, p, "num_terrazas"),
    pick(d, p, "num_terrazas_balcones"),
    pick(d, p, "num_balcones"),
    pick(d, p, "planos"),
    pick(d, p, "inspeccion_ite"),
  ];
  return m2.some(hayDato);
}

function hasEquipamiento(d, p) {
  if (hayDato(d?.eq_observaciones)) return true;
  const activos = EQUIP_PAIRS.filter(([k]) => {
    if (d?.[k] || p?.[k]) return true;
    if (k === "eq_aire" || k === "eq_calefaccion") {
      const id = pick(d, p, `${k}_id`);
      return id != null && id !== "";
    }
    return false;
  });
  if (activos.length) return true;
  return hayEquipamientoSubtexto(
    d,
    p,
    activos.map(([k]) => k)
  );
}

function hasUbicacion(d) {
  if (UB_BOOL.some(([k]) => d?.[k])) return true;
  return (
    hayDato(d?.vistas_nombre) ||
    hayDato(d?.ub_vistas_id) ||
    (Array.isArray(d?.ub_vistas_ids) && d.ub_vistas_ids.length > 0) ||
    hayDato(d?.ub_distancia_mar_obs) ||
    Object.keys(d || {}).some((k) => k.startsWith("ub_") && k.endsWith("_obs") && hayDato(d[k]))
  );
}

function textoVistas(d, p) {
  const nombre = pick(d, p, "vistas_nombre");
  if (hayDato(nombre) && !pareceUuid(nombre)) return String(nombre).trim();
  return null;
}

function pareceUuid(v) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || "").trim());
}

/** Texto compacto exclusiva (duración + fechas). Ignora duración corrupta "true". */
function joinTextoExclusiva(d, p) {
  const parts = [];
  const dur = pick(d, p, "exclusiva_duracion");
  if (dur != null && String(dur).trim() !== "" && String(dur) !== "true" && String(dur) !== "false") {
    parts.push(String(dur).trim());
  }
  const desde = pick(d, p, "exclusiva_desde");
  const hasta = pick(d, p, "exclusiva_hasta");
  if (desde || hasta) {
    parts.push([desde, hasta].filter(Boolean).join(" → "));
  }
  return parts.length ? parts.join(" · ") : "Sí";
}

export function hasGeneralesDistribucionEquipamiento(d, p) {
  return hasGenerales(d, p) || hasDistribucion(d, p) || hasEquipamiento(d, p) || hasUbicacion(d);
}

export function FichaResumenGeneralesDistribucionEquipamiento({ p, d, theme }) {
  const t = theme?.colors || {};
  // Ampliar: chip si bool O si hay tipo real en *_id (merge stale false+Split)
  const chipsEquip = EQUIP_PAIRS.filter(([k]) => {
    if (d?.[k] || p?.[k]) return true;
    if (k === "eq_aire" || k === "eq_calefaccion") {
      const id = pick(d, p, `${k}_id`);
      return id != null && id !== "";
    }
    return false;
  });
  const sups = (d?.superficies || []).filter(superficieFilaTieneDato);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4">
        <CampoResumen theme={theme} label="Operación" valor={pick(d, p, "operacion")} />
        <CampoResumen theme={theme} label="Tipo" valor={pick(d, p, "tipo")} />
        <CampoResumen theme={theme} label="Estado" valor={pick(d, p, "estado_inmueble_nombre") || d.estado_conservacion} />
        <CampoResumen theme={theme} label="Año construcción" valor={pick(d, p, "ano_construccion")} />
        <CampoResumen theme={theme} label="Año reforma" valor={pick(d, p, "ano_reforma")} />
        <CampoResumen theme={theme} label="Dormitorios" valor={pick(d, p, "num_dormitorios")} />
        <CampoResumen theme={theme} label="Baños" valor={pick(d, p, "num_banos")} />
        <CampoResumen theme={theme} label="Aseos" valor={pick(d, p, "num_aseos")} />
        <CampoResumen theme={theme} label="Plantas inmueble" valor={pick(d, p, "plantas_inmueble")} />
        <CampoResumen theme={theme} label="Plantas edificio" valor={pick(d, p, "plantas_edificio")} />
        <CampoResumen theme={theme} label="Altura planta" valor={formatoAlturaPlantaCrm(pick(d, p, "altura_planta"))} />
        <CampoResumen theme={theme} label="Piso" valor={pick(d, p, "piso")} />
        <CampoResumen theme={theme} label="Puerta" valor={pick(d, p, "puerta")} />
        <CampoResumen theme={theme} label="Bloque" valor={pick(d, p, "bloque")} />
        <CampoResumen theme={theme} label="Escalera" valor={pick(d, p, "escalera")} />
        <CampoResumen theme={theme} label="Orientación" valor={pick(d, p, "orientacion_nombre") || pick(d, p, "orientacion")} />
        <CampoResumen theme={theme} label="Cal. energética" valor={pick(d, p, "cal_energetica")} />
        <CampoResumen theme={theme} label="Cédula habitabilidad" valor={pick(d, p, "cedula_habitabilidad") ? "Sí" : null} />
        <CampoResumen theme={theme} label="VPO" valor={pick(d, p, "vpo") ? "Sí" : null} />
        <CampoResumen theme={theme} label="VPO año fin" valor={pick(d, p, "vpo_ano_fin")} />
        <CampoResumen theme={theme} label="Exterior" valor={pick(d, p, "exterior") ? "Sí" : null} />
        <CampoResumen theme={theme} label="Amueblado" valor={pick(d, p, "amueblado") ? "Sí" : null} />
        <CampoResumen
          theme={theme}
          label="Cocina amueblada"
          valor={
            pick(d, p, "cocina_amueblada")
              ? (pick(d, p, "cocina_amueblada_obs") || "Sí")
              : null
          }
        />
        <CampoResumen
          theme={theme}
          label="Armarios"
          valor={
            pick(d, p, "armarios")
              ? (pick(d, p, "armarios_obs") || "Sí")
              : null
          }
        />
        <CampoResumen
          theme={theme}
          label="Exclusiva"
          valor={
            pick(d, p, "exclusiva")
              ? joinTextoExclusiva(d, p)
              : null
          }
        />
        <CampoResumen
          theme={theme}
          label="Suelos"
          valor={pick(d, p, "suelos_nombre") || (pareceUuid(pick(d, p, "suelos_id")) ? null : pick(d, p, "suelos_id"))}
        />
        <CampoResumen
          theme={theme}
          label="Carpintería interior"
          valor={pick(d, p, "carpinteria_int_nombre") || (pareceUuid(pick(d, p, "carpinteria_int_id")) ? null : pick(d, p, "carpinteria_int_id"))}
        />
        <CampoResumen
          theme={theme}
          label="Carpintería exterior"
          valor={pick(d, p, "carpinteria_ext_nombre") || (pareceUuid(pick(d, p, "carpinteria_ext_id")) ? null : pick(d, p, "carpinteria_ext_id"))}
        />
        <CampoResumen theme={theme} label="m² construidos" valor={pick(d, p, "m2_construidos") ? `${pick(d, p, "m2_construidos")} m²` : null} />
        <CampoResumen theme={theme} label="m² útiles" valor={pick(d, p, "m2_utiles") ? `${pick(d, p, "m2_utiles")} m²` : null} />
        <CampoResumen theme={theme} label="m² terrazas" valor={pick(d, p, "m2_terrazas") ? `${pick(d, p, "m2_terrazas")} m²` : null} />
        <CampoResumen theme={theme} label="m² parcela" valor={pick(d, p, "m2_parcela") ? `${pick(d, p, "m2_parcela")} m²` : null} />
        <CampoResumen theme={theme} label="m² jardín" valor={pick(d, p, "m2_jardin") ? `${pick(d, p, "m2_jardin")} m²` : null} />
        <CampoResumen theme={theme} label="Sup. construida (alt.)" valor={pick(d, p, "superficie_construida") ? `${pick(d, p, "superficie_construida")} m²` : null} />
        <CampoResumen theme={theme} label="Sup. útil (alt.)" valor={pick(d, p, "superficie_util") ? `${pick(d, p, "superficie_util")} m²` : null} />
        <CampoResumen theme={theme} label="Terrazas" valor={pick(d, p, "num_terrazas") ?? pick(d, p, "num_terrazas_balcones")} />
        <CampoResumen theme={theme} label="Balcones" valor={pick(d, p, "num_balcones")} />
        <CampoResumen theme={theme} label="Patio" valor={pick(d, p, "num_patio")} />
        <CampoResumen theme={theme} label="Planos" valor={pick(d, p, "planos") ? "Sí" : null} />
        <CampoResumen theme={theme} label="Inspección / ITE" valor={pick(d, p, "inspeccion_ite") ? "Sí" : null} />
        {sups.map((s, i) => (
          <CampoResumen
            key={i}
            theme={theme}
            label={s.nombre || s.tipo || "Planta"}
            valor={s.observaciones || null}
          />
        ))}
        {UB_BOOL.map(([k, lab]) =>
          d?.[k] || p?.[k] ? (
            <CampoResumen
              key={k}
              theme={theme}
              label={lab}
              valor={pick(d, p, `${k}_obs`) || "Sí"}
            />
          ) : null
        )}
        <CampoResumen theme={theme} label="Vistas" valor={textoVistas(d, p)} />
        <CampoResumen theme={theme} label="Obs. distancia mar" valor={d.ub_distancia_mar_obs} />
        <CampoResumen theme={theme} label="Obs. no afectado" valor={d.ub_afectacion_costas_obs} />
      </div>
      {chipsEquip.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-3 mt-2">
          {chipsEquip.map(([eqKey, label]) => {
            const sub = equipamientoSubtexto(eqKey, d, p);
            return (
              <div key={eqKey} className="inline-flex flex-col items-start gap-0.5 max-w-[11rem]">
                <span
                  className="text-xs px-3 py-1 rounded-full font-medium"
                  style={{ background: t.accentGreenLight, color: t.accentGreen }}
                >
                  {label}
                </span>
                {sub && (
                  <span
                    className="text-[11px] leading-snug px-1 font-normal"
                    style={{ color: t.textPrimary || "#ffffff" }}
                  >
                    {sub}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {hayDato(d?.eq_observaciones) && (
        <p className="text-xs leading-relaxed px-1 mt-2 whitespace-pre-wrap" style={{ color: t.textMuted }}>
          {String(d.eq_observaciones).trim()}
        </p>
      )}
    </div>
  );
}
